import { useEffect } from "react";

// Sets document title/description and, optionally, canonical URL, meta keywords,
// Open Graph title/description/url, and JSON-LD structured data blocks.
// Dynamically injected tags are tagged with data-seo so they can be cleaned up
// on navigation without touching the static tags in index.html.
export default function useSEO({ title, description, canonical, keywords, ogTitle, ogDescription, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (selector, attr, name, content) => {
      if (!content) return;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        el.setAttribute("data-seo", "");
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[name="keywords"]', "name", "keywords", keywords);
    setMeta('meta[property="og:title"]', "property", "og:title", ogTitle || title);
    setMeta('meta[property="og:description"]', "property", "og:description", ogDescription || description);
    setMeta('meta[property="og:url"]', "property", "og:url", canonical);
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", ogTitle || title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", ogDescription || description);

    // Canonical link
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    // Remove any build-time prerendered JSON-LD so the client owns a single,
    // always-current set (avoids duplicate structured data during SPA use).
    document.head.querySelectorAll("script[data-prerendered-seo]").forEach((s) => s.remove());

    // JSON-LD structured data (injected + cleaned per page)
    const scripts = [];
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((block) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-seo-jsonld", "");
        s.textContent = JSON.stringify(block);
        document.head.appendChild(s);
        scripts.push(s);
      });
    }

    return () => {
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, canonical, keywords, ogTitle, ogDescription, jsonLd]);
}
